import React, { useState } from 'react';
import { Card, Button } from '../ui';
import { CheckIcon, PlayIcon, UsersIcon, XMarkIcon, UserMinusIcon } from '@heroicons/react/24/outline';

const GroupsBulkActions = ({
  selectedCount,
  clearSelection,
  onGetBasicInfo,
  onBatchAnalysis,
  onJoinGroups,
  onBatchUnsubscribe,
  isLoading
}) => {
  const [massAnalysisSettings, setMassAnalysisSettings] = useState({
    force: false,
    maxConcurrent: 3,
    delayBetween: 2000,
    skipLowQuality: true,
    minMembers: 50
  });

  const [basicInfoSettings, setBasicInfoSettings] = useState({
    maxConcurrent: 2,
    delayBetween: 1000,
    checkHistory: true,
    accountId: ''
  });

  const [joinSettings, setJoinSettings] = useState({
    maxConcurrent: 1,
    delayBetween: 3000,
    maxRetries: 3
  });

  const handleBatchAnalysis = () => {
    onBatchAnalysis(massAnalysisSettings);
  };

  const handleGetBasicInfo = () => {
    onGetBasicInfo(basicInfoSettings);
  };

  const handleJoinGroups = () => {
    onJoinGroups(joinSettings);
  };

  const handleBatchUnsubscribe = () => {
    onBatchUnsubscribe();
  };

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">
            {selectedCount} groups selected
          </span>
          <Button
            size="sm"
            variant="secondary"
            icon={XMarkIcon}
            onClick={clearSelection}
          >
            Clear Selection
          </Button>
        </div>
        <div className="flex space-x-2">
          <Button
            size="sm"
            variant="secondary"
            icon={CheckIcon}
            onClick={handleGetBasicInfo}
            disabled={isLoading.basicInfo}
          >
            {isLoading.basicInfo ? 'Getting Info...' : 'Get Basic Info'}
          </Button>
          <Button
            size="sm"
            variant="primary"
            icon={PlayIcon}
            onClick={handleBatchAnalysis}
            disabled={isLoading.batchAnalysis}
          >
            {isLoading.batchAnalysis ? 'Analyzing...' : 'Run AI Analysis'}
          </Button>
          <Button
            size="sm"
            variant="success"
            icon={UsersIcon}
            onClick={handleJoinGroups}
            disabled={isLoading.joinGroups}
          >
            {isLoading.joinGroups ? 'Joining...' : 'Join Groups'}
          </Button>
          <Button
            size="sm"
            variant="danger"
            icon={UserMinusIcon}
            onClick={handleBatchUnsubscribe}
            disabled={isLoading.batchUnsubscribe}
          >
            {isLoading.batchUnsubscribe ? 'Unsubscribing...' : 'Unsubscribe'}
          </Button>
        </div>
      </div>

      {/* Mass Analysis Settings */}
      <div className="mt-4 p-4 bg-gray-50 rounded-md">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Analysis Settings</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={massAnalysisSettings.force}
                onChange={(e) => setMassAnalysisSettings({
                  ...massAnalysisSettings,
                  force: e.target.checked
                })}
                className="mr-2"
              />
              <span className="text-sm">Force re-analysis</span>
            </label>
          </div>
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={massAnalysisSettings.skipLowQuality}
                onChange={(e) => setMassAnalysisSettings({
                  ...massAnalysisSettings,
                  skipLowQuality: e.target.checked
                })}
                className="mr-2"
              />
              <span className="text-sm">Skip low quality groups</span>
            </label>
          </div>
          <div>
            <label className="text-sm text-gray-600">Min Members:</label>
            <input
              type="number"
              value={massAnalysisSettings.minMembers}
              onChange={(e) => setMassAnalysisSettings({
                ...massAnalysisSettings,
                minMembers: parseInt(e.target.value) || 0
              })}
              className="ml-2 w-20 px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="text-sm text-gray-600">Concurrent:</label>
            <input
              type="number"
              value={massAnalysisSettings.maxConcurrent}
              onChange={(e) => setMassAnalysisSettings({
                ...massAnalysisSettings,
                maxConcurrent: parseInt(e.target.value) || 1
              })}
              className="ml-2 w-16 px-2 py-1 border border-gray-300 rounded text-sm"
              min="1"
              max="5"
            />
          </div>
        </div>

        {/* Basic Info Settings */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Basic Info Settings</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={basicInfoSettings.checkHistory}
                  onChange={(e) => setBasicInfoSettings({
                    ...basicInfoSettings,
                    checkHistory: e.target.checked
                  })}
                  className="mr-2"
                />
                <span className="text-sm">Check message history</span>
              </label>
            </div>
            <div>
              <label className="text-sm text-gray-600">Account ID:</label>
              <input
                type="text"
                value={basicInfoSettings.accountId}
                onChange={(e) => setBasicInfoSettings({
                  ...basicInfoSettings,
                  accountId: e.target.value
                })}
                placeholder="Auto-select"
                className="ml-2 w-24 px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600">Concurrent:</label>
              <input
                type="number"
                value={basicInfoSettings.maxConcurrent}
                onChange={(e) => setBasicInfoSettings({
                  ...basicInfoSettings,
                  maxConcurrent: parseInt(e.target.value) || 1
                })}
                className="ml-2 w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                min="1"
                max="3"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600">Delay (ms):</label>
              <input
                type="number"
                value={basicInfoSettings.delayBetween}
                onChange={(e) => setBasicInfoSettings({
                  ...basicInfoSettings,
                  delayBetween: parseInt(e.target.value) || 1000
                })}
                className="ml-2 w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                min="500"
                step="100"
              />
            </div>
          </div>
        </div>

        {/* Join Settings */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Group Joining Settings</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-gray-600">Concurrent:</label>
              <input
                type="number"
                value={joinSettings.maxConcurrent}
                onChange={(e) => setJoinSettings({
                  ...joinSettings,
                  maxConcurrent: parseInt(e.target.value) || 1
                })}
                className="ml-2 w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                min="1"
                max="2"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600">Delay (ms):</label>
              <input
                type="number"
                value={joinSettings.delayBetween}
                onChange={(e) => setJoinSettings({
                  ...joinSettings,
                  delayBetween: parseInt(e.target.value) || 3000
                })}
                className="ml-2 w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                min="1000"
                step="500"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600">Max Retries:</label>
              <input
                type="number"
                value={joinSettings.maxRetries}
                onChange={(e) => setJoinSettings({
                  ...joinSettings,
                  maxRetries: parseInt(e.target.value) || 3
                })}
                className="ml-2 w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                min="1"
                max="5"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500">
                Conservative settings to avoid rate limits.
                Use 3-5 second delays between joins.
              </p>
            </div>
          </div>
        </div>

        {/* Advanced Settings */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Advanced Settings</h5>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-600">Analysis Delay (ms):</label>
              <input
                type="number"
                value={massAnalysisSettings.delayBetween}
                onChange={(e) => setMassAnalysisSettings({
                  ...massAnalysisSettings,
                  delayBetween: parseInt(e.target.value) || 2000
                })}
                className="ml-2 w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                min="1000"
                step="500"
              />
            </div>
            <div className="col-span-2">
              <p className="text-xs text-gray-500">
                Higher delays reduce API rate limiting but slow down processing.
                Recommended: 2000ms for analysis, 1000ms for basic info.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default GroupsBulkActions;
